import type { UpdateConfigurationModuleByIdV2MutationRequestApi } from '@ingenico/api-sdk-device-management-private-v2';
import { checkIsTableFieldValue } from '@ingenico/react-configuration-generator';
import type { FormBuilderTableValue } from '@ingenico/react-form-builder';
import type {
  FGBuildModel,
  FGGridOnChangeInterceptorOtherAttrs,
  FGInputCustomModel,
  FGInputDateFormat,
  FGInputDateMode,
  FGInputDateModel,
  FGInputModel,
  FGInputMultiSelectModel,
  FGInputNumberModel,
  FGInputSelectModel,
  FGInputsType,
  FGInputTableModel,
  FGInputTextMode,
  FGInputTextModel,
  FGInputTimeFormat,
  FGInputToggleModel,
  FGOnChangeInterceptorOtherAttrs,
  FGOutputModel,
  FGSectionModel,
  FormGeneratorTableValue,
  ObjectRecord,
} from '@ingenico/react-form-generator';
import { EMPTY_VALUE, FGInputTypeEnum, FGLanguagesEnum } from '@ingenico/react-form-generator';
import { Level, showNotification } from '@ingenico/react-notifications';
import type { SmartFieldUiComponentSelectChoiceApi } from '@ingenico/react-smartfield-utils';
import {
  extractFGTableValueForParameterSet,
  MODULE_DETAILS_CUSTOM_ROW_ID,
  parseSingleFieldValue,
} from '@ingenico/react-smartfield-utils';
import { isValidRegex } from '@ingenico/util-common';
import { cloneDeep, get, isEmpty, isEqual, omit, set, toArray, toNumber, toString } from 'lodash-es';
import type {
  ConfigurationModule,
  ConfigurationModuleParameterSetValue,
  TemplateValues,
} from '../../../domain/models/configurationModules.model';
import type { SmartFieldsResource } from '../../../domain/models/smartFields.model';
import type {
  EnumSmartFieldDataType,
  SmartFieldUiComponentDateTimeOptions,
  SmartFieldUiComponentInputStringOptions,
  TemplateDetailedResource,
  TemplateDetailedResourceParameter,
  TemplateDetailedResourceParameterContainer,
  TemplateDetailedResourceParameterField,
  TemplateListResource,
  TemplateResourceParameterFieldChild,
  TemplateSimplifiedResource,
} from '../../../domain/models/templateResource.model';
import type { ParameterModuleContentParametersModalSection } from '../parameter-module-content-parameters-modal/ParameterModuleContentParametersModal.ctrl';
import type { ParameterModuleHeaderParameter } from '../parameter-module-header/ParameterModuleHeader.model';
import type {
  DependantFieldsInfo,
  DirtyParameterModuleContentFieldMapping,
  DirtyParameterModuleFieldMapping,
  DirtyParameterModuleState,
  ParameterModuleContentFieldMapping,
  ParameterModuleFieldMapping,
  ParameterModuleFieldMappingAttributes,
  ParameterModuleFieldMappingState,
  ParameterModuleTemplateDetailedResourceParameterField,
  ParameterModuleTemplateFieldId,
  PreviewModalState,
  SelectedDependantFields,
} from './ParameterModuleTab.model';

function getDefaultMultiSelectFieldValues(
  selectedIds?: number[],
  choices?: SmartFieldUiComponentSelectChoiceApi[],
): string[] | undefined {
  return selectedIds?.reduce((accum, selectedId) => {
    const option = choices?.find(({ id: choiceId }) => choiceId === selectedId);
    return option ? [...accum, option.value] : accum;
  }, [] as string[]);
}

function getDefaultSingleSelectFieldValues(
  selectedId?: number,
  choices?: SmartFieldUiComponentSelectChoiceApi[],
): string | undefined {
  return selectedId && choices ? choices?.find(({ id }) => isEqual(id, selectedId))?.value : undefined;
}

function getCurrentValue(
  moduleDetailsFieldMapping: ParameterModuleFieldMapping,
  inputKey: string,
): ParameterModuleFieldMappingState['value'] {
  return moduleDetailsFieldMapping[inputKey]?.dirty
    ? moduleDetailsFieldMapping[inputKey]?.dirty?.value
    : moduleDetailsFieldMapping[inputKey]?.initial?.value;
}

export function getSelectableComponentType(parameterField: TemplateDetailedResourceParameterField): FGInputTypeEnum {
  const {
    definition: { common },
  } = parameterField;
  const [uiCompnentType] = Object.keys(common?.uiComponentOptions ?? {});
  return uiCompnentType === 'multiSelect' ? FGInputTypeEnum.MultiSelect : FGInputTypeEnum.Select;
}

export function getTextComponentType(parameterField: TemplateDetailedResourceParameterField): FGInputTypeEnum {
  const {
    definition: { common },
  } = parameterField;
  const [uiComponentType] = Object.keys(common?.uiComponentOptions ?? {});

  switch (uiComponentType) {
    case 'multiSelect': {
      return FGInputTypeEnum.MultiSelect;
    }
    case 'select': {
      return FGInputTypeEnum.Select;
    }
    case 'dateTime': {
      return FGInputTypeEnum.Date;
    }
    default: {
      return FGInputTypeEnum.Text;
    }
  }
}

export function getComponentType(parameterField: TemplateDetailedResourceParameterField): FGInputTypeEnum {
  switch (parameterField.dataType) {
    case 'BOOLEAN': {
      return FGInputTypeEnum.Toggle;
    }
    case 'NUMBER': {
      return FGInputTypeEnum.Number;
    }
    case 'STRUCTURE': {
      return FGInputTypeEnum.Table;
    }
    case 'ENUM': {
      return getSelectableComponentType(parameterField);
    }
    default: {
      return getTextComponentType(parameterField);
    }
  }
}

function getTemplateContainers(
  templateDetailedResourceParameter?: TemplateDetailedResourceParameter,
): Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>> {
  return (
    templateDetailedResourceParameter?.fields?.reduce(
      (accum, field) => ({
        ...accum,
        ...(field.containerId
          ? {
              [field.containerId]: {
                ...accum[field.containerId],
                [field.id]: { ...field, componentType: getComponentType(field) },
              },
            }
          : {}),
      }),
      {} as Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>,
    ) ?? {}
  );
}

export function createFieldMappingByContainerId(
  templateMapping?: Record<ParameterModuleTemplateFieldId, TemplateDetailedResource | TemplateSimplifiedResource>,
): Record<
  ParameterModuleTemplateFieldId,
  Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>
> {
  return (
    Object.keys(templateMapping ?? {}).reduce(
      (accum, templateId) => ({
        ...accum,
        [templateId]: getTemplateContainers(templateMapping?.[templateId]?.parameter),
      }),
      {} as Record<
        ParameterModuleTemplateFieldId,
        Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>
      >,
    ) ?? {}
  );
}

export function getTextInputType(field?: SmartFieldUiComponentInputStringOptions): FGInputTextMode {
  switch (field?.type) {
    case 'SECRET': {
      return 'password';
    }
    default: {
      return 'text';
    }
  }
}

export function getDatetimeFieldMode(field?: SmartFieldUiComponentDateTimeOptions): FGInputDateMode {
  switch (field?.type) {
    case 'DATE_TIME': {
      return 'datetime';
    }
    case 'TIME': {
      return 'time';
    }
    default: {
      return 'date';
    }
  }
}

function getMultiSelectFieldInitialValue(
  initialValue?: string[],
  defaultSelectedIds?: number[],
  choices?: SmartFieldUiComponentSelectChoiceApi[],
): string[] | undefined {
  return initialValue ?? getDefaultMultiSelectFieldValues(defaultSelectedIds, choices);
}

function getMultiSelectFieldValue(
  fieldId: string,
  valuesMapping?: ParameterModuleFieldMapping,
  defaultSelectedIds?: number[],
  choices?: SmartFieldUiComponentSelectChoiceApi[],
): string[] | undefined {
  const initialValue = valuesMapping?.[fieldId]?.initial?.value as string[] | undefined;
  const dirtyValue = valuesMapping?.[fieldId]?.dirty?.value as string[] | undefined;
  return valuesMapping?.[fieldId]?.dirty
    ? dirtyValue
    : getMultiSelectFieldInitialValue(initialValue, defaultSelectedIds, choices);
}

function filterDefaultValuesFromMultiSelect(
  selectedDefaultValues: string[] | undefined,
  choices?: SmartFieldUiComponentSelectChoiceApi[],
): string[] | undefined {
  if ((choices ?? []).length > 0 && (selectedDefaultValues ?? []).length > 0) {
    return (selectedDefaultValues ?? []).reduce((acc, selectedDefaultValue: string) => {
      const matchingOption = (choices ?? []).find((choice) => toString(choice.value) === selectedDefaultValue);
      if (matchingOption) {
        acc.push(matchingOption.value);
      }
      return acc;
    }, [] as string[]);
  }
  return [];
}

function filterDefaultValueFromSelectWithValue(
  selectedDefaultValue: string | undefined,
  choices?: SmartFieldUiComponentSelectChoiceApi[],
): string | undefined {
  if ((choices ?? []).length > 0 && selectedDefaultValue) {
    const matchingOption = (choices ?? []).find((choice) => toString(choice.value) === toString(selectedDefaultValue));
    if (matchingOption) {
      return matchingOption.value;
    }
  }
  return undefined;
}

function filterDefaultValueFromSelectWithDefaultSelectId(
  selectedDefaultId: number | undefined,
  choices?: SmartFieldUiComponentSelectChoiceApi[],
): string | undefined {
  if ((choices ?? []).length > 0 && typeof selectedDefaultId === 'number') {
    const matchingOption = (choices ?? []).find((choice) => choice.id === selectedDefaultId);
    if (matchingOption) {
      return matchingOption.value;
    }
  }
  return undefined;
}

function getMultiSelectField(
  { definition: { common, dynamicChoice }, id, isRequired, name, description }: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
): FGInputMultiSelectModel {
  const multiSelectFieldOption = common?.uiComponentOptions?.multiSelect;
  const fieldValue = getMultiSelectFieldValue(
    id,
    valuesMapping,
    multiSelectFieldOption?.defaultSelectedIds,
    multiSelectFieldOption?.choices,
  );
  const multiSelectedValue = ensureSimpleValueIsNotSmartField(fieldValue);
  const currentValue = multiSelectedValue ?? EMPTY_VALUE;
  return {
    type: FGInputTypeEnum.MultiSelect,
    placeholder: multiSelectFieldOption?.placeholder,
    inputOptions: toArray(multiSelectFieldOption?.choices),
    rules: {
      maxLength: multiSelectFieldOption?.max,
      minLength: multiSelectFieldOption?.min,
      required: isRequired || multiSelectFieldOption?.isRequired,
    },
    inheritedValue: EMPTY_VALUE,
    localizedValue: EMPTY_VALUE,
    currentLocalizedValue: currentValue,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
    label: name,
    hint: description ?? multiSelectFieldOption?.hint,
    dynamicOptionLabelSource: dynamicChoice?.fieldLabelId,
    dynamicOptionsSource: dynamicChoice?.fieldId,
    dynamicOptionValueSource: dynamicChoice?.fieldValueId,
    inputDefaultValue: multiSelectedValue,
  } satisfies FGInputMultiSelectModel;
}

export function getSimpleSelectField(
  { definition: { common, dynamicChoice }, id, isRequired, name, description }: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
): FGInputSelectModel {
  const selectField = common?.uiComponentOptions?.select;
  const defaultSingleSelectTemplateValue = getDefaultSingleSelectFieldValues(
    selectField?.defaultSelectedId,
    selectField?.choices,
  );
  const fieldValue = valuesMapping?.[id]?.dirty
    ? (valuesMapping?.[id]?.dirty?.value as string | undefined)
    : ((valuesMapping?.[id]?.initial?.value ?? defaultSingleSelectTemplateValue) as string | undefined);
  const singleSelectValue = ensureSimpleValueIsNotSmartField(fieldValue);
  const currentValue = singleSelectValue ?? EMPTY_VALUE;
  return {
    type: FGInputTypeEnum.Select,
    inputOptions: toArray(selectField?.choices),
    placeholder: selectField?.placeholder,
    rules: {
      required: isRequired || selectField?.isRequired,
    },
    inheritedValue: EMPTY_VALUE,
    localizedValue: EMPTY_VALUE,
    currentLocalizedValue: currentValue,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
    label: name,
    hint: description ?? selectField?.hint,
    dynamicOptionLabelSource: dynamicChoice?.fieldLabelId,
    dynamicOptionsSource: dynamicChoice?.fieldId,
    dynamicOptionValueSource: dynamicChoice?.fieldValueId,
    inputDefaultValue: singleSelectValue,
  } satisfies FGInputSelectModel;
}

function getDatetimeField(
  { definition: { common }, id, isRequired, name, description }: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
): FGInputDateModel {
  const dateField = common?.uiComponentOptions?.dateTime;
  const value = valuesMapping?.[id]?.dirty
    ? (valuesMapping?.[id]?.dirty?.value as string | undefined)
    : ((valuesMapping?.[id]?.initial?.value ?? dateField?.defaultValue) as string | undefined);
  const dateValue = ensureSimpleValueIsNotSmartField<string | undefined>(value);
  return {
    type: FGInputTypeEnum.Date,
    mode: getDatetimeFieldMode(dateField),
    rules: {
      required: isRequired || dateField?.isRequired,
    },
    inputFormat: dateField?.format as FGInputDateFormat | FGInputTimeFormat,
    inheritedValue: EMPTY_VALUE,
    localizedValue: EMPTY_VALUE,
    currentLocalizedValue: dateValue ?? EMPTY_VALUE,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
    label: name,
    hint: description ?? dateField?.hint,
    inputDefaultValue: dateValue,
  } satisfies FGInputDateModel;
}

function getTextField(
  { definition: { common, dynamicChoice }, id, isRequired, name, description }: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
): FGInputTextModel {
  const stringFieldOption = common?.uiComponentOptions?.inputString;
  const value = valuesMapping?.[id]?.dirty
    ? (valuesMapping?.[id]?.dirty?.value as string | undefined)
    : ((valuesMapping?.[id]?.initial?.value ?? stringFieldOption?.defaultValue) as string | undefined);
  const textFieldValue = ensureSimpleValueIsNotSmartField<string | undefined>(value);
  const scapedTextFieldValue = typeof textFieldValue === 'object' ? undefined : textFieldValue;
  return {
    type: FGInputTypeEnum.Text,
    inputType: getTextInputType(stringFieldOption),
    placeholder: stringFieldOption?.placeholder,
    rules: {
      minLength: stringFieldOption?.minLength,
      maxLength: stringFieldOption?.maxLength,
      required: isRequired || stringFieldOption?.isRequired,
      pattern: (() => {
        const patternValue = stringFieldOption?.pattern?.value;
        // ReDoS protection: reject long patterns or patterns with nested quantifiers
        if (!patternValue || patternValue.length > 10_000 || /(\+|\*|\?)\s*\)(\+|\*|\?)/.test(patternValue)) {
          return undefined;
        }
        try {
          return isValidRegex(patternValue) ? new RegExp(patternValue, 'u') : undefined;
        } catch {
          return undefined;
        }
      })(),
    },
    inheritedValue: EMPTY_VALUE,
    localizedValue: EMPTY_VALUE,
    currentLocalizedValue: scapedTextFieldValue ?? EMPTY_VALUE,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
    label: name,
    hint: description ?? stringFieldOption?.hint,
    dynamicOptionLabelSource: dynamicChoice?.fieldLabelId,
    dynamicOptionsSource: dynamicChoice?.fieldId,
    dynamicOptionValueSource: dynamicChoice?.fieldValueId,
    inputDefaultValue: scapedTextFieldValue,
  } satisfies FGInputTextModel;
}

function generateSelectableField(
  parameterField: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
): FGInputCustomModel {
  const {
    definition: { common },
  } = parameterField;
  const [uiCompnentType] = Object.keys(common?.uiComponentOptions ?? {});

  return uiCompnentType === 'multiSelect'
    ? getMultiSelectField(parameterField, valuesMapping)
    : getSimpleSelectField(parameterField, valuesMapping);
}

function generateInputTextCustomConfig(
  parameterField: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
): FGInputCustomModel {
  const {
    definition: { common },
  } = parameterField;
  const [uiComponentType] = Object.keys(common?.uiComponentOptions ?? {});

  switch (uiComponentType) {
    case 'multiSelect': {
      return getMultiSelectField(parameterField, valuesMapping);
    }
    case 'select': {
      return getSimpleSelectField(parameterField, valuesMapping);
    }
    case 'dateTime': {
      return getDatetimeField(parameterField, valuesMapping);
    }
    default: {
      return getTextField(parameterField, valuesMapping);
    }
  }
}

function checkIsSmartField(value: unknown): value is SmartFieldsResource {
  return !!(value as { readonly id: string })?.id && !!(value as { readonly name: string })?.name;
}

function ensureSimpleValueIsNotSmartField<T>(value: T): T | undefined {
  return checkIsSmartField(value) ? undefined : value;
}

function generateToggleInputCustomConfig(
  { definition: { common }, id, name, description, isRequired }: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
): FGInputToggleModel {
  const toggleField = common?.uiComponentOptions?.toggle;
  const value = valuesMapping?.[id]?.dirty
    ? (valuesMapping?.[id]?.dirty?.value as boolean | undefined)
    : ((valuesMapping?.[id]?.initial?.value ?? toggleField?.defaultValue) as boolean | undefined);
  const toggleFieldValue = ensureSimpleValueIsNotSmartField<boolean | undefined>(value);
  return {
    type: FGInputTypeEnum.Toggle,
    hint: description ?? toggleField?.hint,
    rules: { required: isRequired },
    inheritedValue: EMPTY_VALUE,
    localizedValue: EMPTY_VALUE,
    currentLocalizedValue: toggleFieldValue ?? EMPTY_VALUE,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
    label: name,
    inputDefaultValue: toggleFieldValue,
  } satisfies FGInputToggleModel;
}

function generateNumberInputCustomConfig(
  { definition: { common }, id, isRequired, name, description }: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
): FGInputNumberModel {
  const numberField = common?.uiComponentOptions?.inputNumber;
  const value = valuesMapping?.[id]?.dirty
    ? (valuesMapping?.[id]?.dirty?.value as number | string | undefined)
    : ((valuesMapping?.[id]?.initial?.value ?? numberField?.defaultValue) as number | string | undefined);
  const inputNumberValue = ensureSimpleValueIsNotSmartField<number | string | undefined>(value);
  return {
    type: FGInputTypeEnum.Number,
    rules: {
      max: numberField?.max,
      min: numberField?.min,
      required: isRequired || numberField?.isRequired,
    },
    inheritedValue: EMPTY_VALUE,
    localizedValue: EMPTY_VALUE,
    currentLocalizedValue: inputNumberValue ?? EMPTY_VALUE,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
    label: name,
    hint: description ?? numberField?.hint,
    inputDefaultValue: inputNumberValue === undefined ? inputNumberValue : (toNumber(inputNumberValue) as number),
  } as unknown as FGInputNumberModel;
}

function generateFGSchemaFromStructure(
  structureFields: TemplateResourceParameterFieldChild[],
  valuesMapping?: ParameterModuleFieldMapping,
  disabled?: boolean,
): FGInputTableModel<FGInputModel>['inputsSchema'] {
  return toArray(
    structureFields?.map((field) => {
      return {
        disabled: !!disabled,
        inputKey: field.id,
        inputLabel: field.name,
        description: field.description,
        inputCustomConfig: manageInputCustomConfig(field, valuesMapping),
      };
    }),
  );
}

function getStructureFieldCurrentValue(
  id: string,
  valuesMapping?: ParameterModuleFieldMapping,
  isSmartField?: boolean,
): FormGeneratorTableValue | undefined {
  if (isSmartField) {
    return valuesMapping?.[id]?.initial?.value as FormGeneratorTableValue | undefined;
  } else {
    return valuesMapping?.[id]?.dirty
      ? (valuesMapping?.[id]?.dirty?.value as FormGeneratorTableValue | undefined)
      : (valuesMapping?.[id]?.initial?.value as FormGeneratorTableValue | undefined);
  }
}

function generateStructureInputCustomConfig(
  { definition: { structure }, id, isRequired }: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
  disabled?: boolean,
  config?: {
    readonly hint?: string;
    readonly showGridOverlay?: boolean;
    readonly isSmartField?: boolean;
    readonly smartFieldPrefix: string;
  },
): FGInputCustomModel {
  const value = getStructureFieldCurrentValue(id, valuesMapping, config?.isSmartField);

  return {
    type: FGInputTypeEnum.Table,
    rules: { required: isRequired, max: structure?.maxRows, min: structure?.minRows },
    inputsSchema: generateFGSchemaFromStructure(toArray(structure?.childFields), valuesMapping, disabled),
    inheritedValue: EMPTY_VALUE,
    localizedValue: EMPTY_VALUE,
    currentLocalizedValue: value ?? EMPTY_VALUE,
    isManuallyLocalizedByUser: false,
    isManuallyRestoredToInheritedByUser: false,
    hint: config?.hint ? `${config?.smartFieldPrefix}: ${config?.hint}` : undefined,
    showGridOverlay: config?.showGridOverlay,
    inputDefaultValue: value,
  } satisfies FGInputTableModel<FGInputModel>;
}

function manageInputCustomConfig(
  field: TemplateDetailedResourceParameterField,
  valuesMapping?: ParameterModuleFieldMapping,
  disabled?: boolean,
): FGInputCustomModel {
  if (field.elementType === 'DYNAMIC_CHOICE') {
    return getTextField(field, valuesMapping);
  }

  switch (field.dataType) {
    case 'BOOLEAN': {
      return generateToggleInputCustomConfig(field, valuesMapping);
    }
    case 'NUMBER': {
      return generateNumberInputCustomConfig(field, valuesMapping);
    }
    case 'STRUCTURE': {
      return generateStructureInputCustomConfig(field, valuesMapping, disabled);
    }
    case 'ENUM': {
      return generateSelectableField(field, valuesMapping);
    }
    default: {
      return generateInputTextCustomConfig(field, valuesMapping);
    }
  }
}

/**
 * Formats a SmartField display value with a prefix.
 * Falls back to 'smartfield mapped' if name is not available (for text fields only).
 */
function getSmartFieldDisplayValue(
  smartField: SmartFieldsResource | undefined,
  prefix: string,
  useGenericFallback = false,
): string {
  if (!smartField) {
    return '';
  }

  if (smartField.name) {
    return `${prefix}: ${toString(smartField.name)}`;
  }

  // For text fields, use generic fallback instead of ID
  if (useGenericFallback && smartField.id) {
    return `${prefix}: smartfield mapped`;
  }

  return '';
}

function getSmartFieldInputConfig({
  field,
  smartField,
  valuesMapping,
  smartFieldPrefix,
}: {
  readonly field: TemplateDetailedResourceParameterField;
  readonly smartField?: SmartFieldsResource;
  readonly valuesMapping?: ParameterModuleFieldMapping;
  readonly smartFieldPrefix: string;
}): FGBuildModel['sections'][number]['inputs'][number]['inputCustomConfig'] {
  switch (field.dataType) {
    case 'STRUCTURE': {
      return generateStructureInputCustomConfig(field, valuesMapping, true, {
        hint: smartField?.name,
        showGridOverlay: true,
        isSmartField: true,
        smartFieldPrefix,
      });
    }
    case 'BOOLEAN': {
      return {
        type: FGInputTypeEnum.Toggle,
        hint: field?.description ?? field?.definition?.common?.uiComponentOptions?.toggle?.hint,
        rules: { required: field?.isRequired },
        inheritedValue: EMPTY_VALUE,
        localizedValue: EMPTY_VALUE,
        currentLocalizedValue: false,
        isManuallyLocalizedByUser: false,
        isManuallyRestoredToInheritedByUser: false,
        label: field?.name,
        secondaryText: smartField?.name ? `${smartFieldPrefix}: ${smartField?.name}` : undefined,
      } satisfies FGInputToggleModel;
    }
    default: {
      // For text fields, use generic 'smartfield mapped' fallback if name is not available
      const displayValue = getSmartFieldDisplayValue(smartField, smartFieldPrefix, true);

      return {
        type: FGInputTypeEnum.Text,
        inputType: 'text',
        inheritedValue: EMPTY_VALUE,
        localizedValue: EMPTY_VALUE,
        currentLocalizedValue: displayValue,
        isManuallyLocalizedByUser: false,
        isManuallyRestoredToInheritedByUser: false,
        rules: {
          required: field.isRequired,
        },
        label: field.name,
        hint: field.description,
      } satisfies FGInputTextModel;
    }
  }
}

function getSmartFieldInputConfigForVirtualDevice({
  field,
  smartField,
  valuesMapping,
  smartFieldPrefix,
  disabled: _disabled = false,
}: {
  readonly field: TemplateDetailedResourceParameterField;
  readonly smartField?: SmartFieldsResource;
  readonly valuesMapping?: ParameterModuleFieldMapping;
  readonly smartFieldPrefix: string;
  readonly disabled?: boolean;
}): FGBuildModel['sections'][number]['inputs'][number]['inputCustomConfig'] {
  const output = getSmartFieldInputConfig({ field, smartField, valuesMapping, smartFieldPrefix });
  const smartfieldName = getSmartFieldDisplayValue(smartField, smartFieldPrefix, true);

  switch (field.dataType) {
    case 'STRUCTURE': {
      return {
        ...(output as FGInputTableModel<FGInputModel>),
        hint: '',
        showGridOverlay: false,
        hyperlinkLabel: smartfieldName,
      };
    }
    case 'BOOLEAN': {
      return {
        ...(output as FGInputToggleModel),
        currentLocalizedValue: Boolean(
          valuesMapping?.[field.id]?.initial.smartFieldDefault ?? valuesMapping?.[field.id]?.initial.templateDefault,
        ),
        hyperlinkLabel: smartfieldName,
        secondaryText: '',
      } satisfies FGInputToggleModel;
    }

    default: {
      return {
        ...(output as FGInputTextModel),
        currentLocalizedValue: toString(
          valuesMapping?.[field.id]?.initial.smartFieldDefault ?? valuesMapping?.[field.id]?.initial.templateDefault,
        ),
        hyperlinkLabel: smartfieldName,
      } satisfies FGInputTextModel;
    }
  }
}

function getDisplayedSmartField({
  field,
  smartField,
  valuesMapping,
  smartFieldPrefix,
}: {
  readonly field: TemplateDetailedResourceParameterField;
  readonly smartField?: SmartFieldsResource;
  readonly valuesMapping?: ParameterModuleFieldMapping;
  readonly smartFieldPrefix: string;
}): FGBuildModel['sections'][number]['inputs'][number] {
  return {
    disabled: field.dataType !== 'STRUCTURE',
    inputKey: field.id,
    inputLabel: field.name,
    smartFieldHyperlinkId: toString(smartField?.id),
    description: field.description,
    inputCustomConfig: getSmartFieldInputConfig({ field, smartField, valuesMapping, smartFieldPrefix }),
  };
}
function getDisplayedSmartFieldForVirtualDevice({
  field,
  smartField,
  valuesMapping,
  smartFieldPrefix,
}: {
  readonly field: TemplateDetailedResourceParameterField;
  readonly smartField?: SmartFieldsResource;
  readonly valuesMapping?: ParameterModuleFieldMapping;
  readonly smartFieldPrefix: string;
}): FGBuildModel['sections'][number]['inputs'][number] {
  return {
    disabled: field.dataType !== 'STRUCTURE',
    inputKey: field.id,
    inputLabel: field.name,
    smartFieldHyperlinkId: toString(smartField?.id),
    description: field.description,
    inputCustomConfig: getSmartFieldInputConfigForVirtualDevice({
      field,
      smartField,
      valuesMapping,
      smartFieldPrefix,
      disabled: field.dataType !== 'STRUCTURE',
    }),
  };
}

function validateSmartField(
  fieldId: string,
  valuesMapping?: ParameterModuleFieldMapping,
  smartFields?: Record<string, SmartFieldsResource>,
): {
  isSmartField: boolean;
  smartField?: SmartFieldsResource;
} {
  const isSmartField = !!smartFields?.[fieldId];
  return {
    isSmartField,
    smartField:
      (valuesMapping?.[fieldId]?.dirty?.value as SmartFieldsResource) ??
      (valuesMapping?.[fieldId]?.initial?.value as SmartFieldsResource),
  };
}

function transformParameterFieldForConfigBuilder({
  field,
  smartFields,
  valuesMapping,
  disabled = false,
  smartFieldPrefix,
}: {
  readonly field: TemplateDetailedResourceParameterField;
  readonly smartFields?: Record<string, SmartFieldsResource>;
  readonly valuesMapping?: ParameterModuleFieldMapping;
  readonly disabled?: boolean;
  readonly smartFieldPrefix: string;
}): FGBuildModel['sections'][number]['inputs'][number] {
  const { isSmartField, smartField } = validateSmartField(field.id, valuesMapping, smartFields);

  return isSmartField
    ? getDisplayedSmartField({ field, smartField, valuesMapping, smartFieldPrefix })
    : {
        disabled,
        inputKey: field.id,
        inputLabel: field.name,
        description: field.description,
        inputCustomConfig: manageInputCustomConfig(field, valuesMapping, disabled),
      };
}
function transformParameterFieldForConfigBuilderForVirtualDevice({
  field,
  smartFields,
  valuesMapping,
  disabled = false,
  smartFieldPrefix,
}: {
  readonly field: TemplateDetailedResourceParameterField;
  readonly smartFields?: Record<string, SmartFieldsResource>;
  readonly valuesMapping?: ParameterModuleFieldMapping;
  readonly disabled?: boolean;
  readonly smartFieldPrefix: string;
}): FGBuildModel['sections'][number]['inputs'][number] {
  const { isSmartField, smartField } = validateSmartField(field.id, valuesMapping, smartFields);

  return isSmartField
    ? getDisplayedSmartFieldForVirtualDevice({ field, smartField, valuesMapping, smartFieldPrefix })
    : {
        disabled,
        inputKey: field.id,
        inputLabel: field.name,
        description: field.description,
        inputCustomConfig: manageInputCustomConfig(field, valuesMapping, disabled),
      };
}

function getSectionFieldsFromMapping({
  id,
  fieldsMapping,
  smartFieldPrefix,
  smartFields,
  valuesMapping,
  disabled = false,
}: {
  readonly id: string;
  readonly fieldsMapping: Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>;
  readonly smartFieldPrefix: string;
  readonly smartFields?: Record<string, SmartFieldsResource>;
  readonly valuesMapping?: ParameterModuleFieldMapping;
  readonly disabled?: boolean;
}): FGBuildModel['sections'] {
  return [
    {
      disabled,
      id,
      inputs: Object.values(fieldsMapping[id] ?? {}).map((field) =>
        transformParameterFieldForConfigBuilder({ field, smartFields, valuesMapping, disabled, smartFieldPrefix }),
      ),
    },
  ];
}
function getSectionFieldsFromMappingForVirtualDevice({
  id,
  fieldsMapping,
  smartFieldPrefix,
  smartFields,
  valuesMapping,
  disabled = false,
}: {
  readonly id: string;
  readonly fieldsMapping: Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>;
  readonly smartFieldPrefix: string;
  readonly smartFields?: Record<string, SmartFieldsResource>;
  readonly valuesMapping?: ParameterModuleFieldMapping;
  readonly disabled?: boolean;
}): FGBuildModel['sections'] {
  return [
    {
      disabled,
      id,
      inputs: Object.values(fieldsMapping[id] ?? {}).map((field) =>
        transformParameterFieldForConfigBuilderForVirtualDevice({
          field,
          smartFields,
          valuesMapping,
          disabled,
          smartFieldPrefix,
        }),
      ),
    },
  ];
}

function extractCellValue(
  cell: NonNullable<TemplateValues[number]['values'][number]['fieldValue']['structure']>[number]['cells'][number],
): FGInputsType {
  switch (cell.fieldValue.type) {
    case 'SMART_FIELD': {
      return undefined;
    }
    case 'STRUCTURE': {
      return getStructuredFieldValue(cell.columnId, cell.fieldValue.structure);
    }
    default: {
      return cell.fieldValue.single;
    }
  }
}

function buildTableFieldLine(
  structureElementValue: NonNullable<TemplateValues[number]['values'][number]['fieldValue']['structure']>[number],
) {
  return structureElementValue.cells?.reduce((accum, cell) => {
    return {
      ...accum,
      [cell.columnId]: extractCellValue(cell),
    };
  }, {});
}

function getStructuredFieldValue(
  fieldId: string,
  structureFieldValue?: TemplateValues[number]['values'][number]['fieldValue']['structure'],
): FormGeneratorTableValue {
  return {
    [fieldId]: {
      type: 'TABLE',
      rows:
        structureFieldValue?.reduce(
          (accum, current, index) => ({
            ...accum,
            [index]: {
              [MODULE_DETAILS_CUSTOM_ROW_ID]: current.rowId,
              ...buildTableFieldLine(current),
            },
          }),
          {},
        ) ?? {},
    },
  };
}

function getMultiSelectValue(value?: string): string[] | undefined {
  if (value) {
    if (Array.isArray(value)) {
      return value;
    } else {
      const data = JSON.parse(value) as string[];
      if (Array.isArray(data)) {
        return data;
      }
    }
    return [];
  }
  return undefined;
}

function getTemplateFieldValueByComponentType(
  componentType?: FGInputTypeEnum,
  fieldAttributes?: ParameterModuleTemplateDetailedResourceParameterField,
): FGInputsType | SmartFieldsResource | undefined {
  switch (componentType) {
    case FGInputTypeEnum.Toggle: {
      return fieldAttributes?.definition?.common?.uiComponentOptions?.toggle?.defaultValue;
    }
    case FGInputTypeEnum.MultiSelect: {
      return getDefaultMultiSelectFieldValues(
        fieldAttributes?.definition?.common?.uiComponentOptions?.multiSelect?.defaultSelectedIds,
        fieldAttributes?.definition?.common?.uiComponentOptions?.multiSelect?.choices,
      );
    }
    case FGInputTypeEnum.Number: {
      return fieldAttributes?.definition?.common?.uiComponentOptions?.inputNumber?.defaultValue;
    }
    case FGInputTypeEnum.Date: {
      return fieldAttributes?.definition?.common?.uiComponentOptions?.dateTime?.defaultValue;
    }
    case FGInputTypeEnum.Select: {
      return getDefaultSingleSelectFieldValues(
        fieldAttributes?.definition?.common?.uiComponentOptions?.select?.defaultSelectedId,
        fieldAttributes?.definition?.common?.uiComponentOptions?.select?.choices,
      );
    }
    default: {
      return fieldAttributes?.definition?.common?.uiComponentOptions?.inputString?.defaultValue;
    }
  }
}

function manageTextFieldValues(
  componentType?: FGInputTypeEnum,
  value?: string,
  fieldAttributes?: ParameterModuleTemplateDetailedResourceParameterField,
): FGInputsType {
  switch (componentType) {
    case FGInputTypeEnum.Toggle: {
      const initialValue = value ?? fieldAttributes?.definition?.common?.uiComponentOptions?.toggle?.defaultValue;
      return initialValue === 'true';
    }
    case FGInputTypeEnum.MultiSelect: {
      return filterDefaultValuesFromMultiSelect(
        getMultiSelectValue(value) ?? [],
        fieldAttributes?.definition?.common?.uiComponentOptions?.multiSelect?.choices,
      );
    }
    case FGInputTypeEnum.Number: {
      return value ?? fieldAttributes?.definition?.common?.uiComponentOptions?.inputNumber?.defaultValue;
    }
    case FGInputTypeEnum.Date: {
      return value ?? fieldAttributes?.definition?.common?.uiComponentOptions?.dateTime?.defaultValue;
    }
    case FGInputTypeEnum.Select: {
      return value
        ? filterDefaultValueFromSelectWithValue(
            value,
            fieldAttributes?.definition?.common?.uiComponentOptions?.select?.choices,
          )
        : filterDefaultValueFromSelectWithDefaultSelectId(
            fieldAttributes?.definition?.common?.uiComponentOptions?.select?.defaultSelectedId,
            fieldAttributes?.definition?.common?.uiComponentOptions?.select?.choices,
          );
    }
    default: {
      return value ?? fieldAttributes?.definition?.common?.uiComponentOptions?.inputString?.defaultValue ?? undefined;
    }
  }
}

function manageIncomingFieldValues(
  valueToTransform?: TemplateValues[number]['values'][number],
  fieldAttributes?: ParameterModuleTemplateDetailedResourceParameterField,
): FGInputsType {
  switch (fieldAttributes?.dataType) {
    case 'STRUCTURE': {
      return getStructuredFieldValue(toString(fieldAttributes?.id), valueToTransform?.fieldValue?.structure);
    }
    default: {
      return manageTextFieldValues(
        fieldAttributes?.componentType,
        valueToTransform?.fieldValue.single,
        fieldAttributes,
      );
    }
  }
}

function transformTemplateValues(
  valueToTransform?: TemplateValues[number]['values'][number],
  fieldAttributes?: ParameterModuleTemplateDetailedResourceParameterField,
): FGInputsType | SmartFieldsResource {
  return valueToTransform?.fieldValue.type === 'SMART_FIELD'
    ? (valueToTransform.fieldValue.smartField as SmartFieldsResource)
    : manageIncomingFieldValues(valueToTransform, fieldAttributes);
}

function extractDefaultValueFromTemplate(
  fieldAttributes?: ParameterModuleTemplateDetailedResourceParameterField,
): FGInputsType | SmartFieldsResource | undefined {
  return getTemplateFieldValueByComponentType(fieldAttributes?.componentType, fieldAttributes);
}

function getValuesFromTemplates(
  fieldAttributes?: ParameterModuleTemplateDetailedResourceParameterField,
  incomingConfigModuleValue?: TemplateValues[number]['values'][number],
  dirtyFieldValue?: DirtyParameterModuleState,
): ParameterModuleFieldMappingAttributes {
  const defaultType = fieldAttributes?.componentType ?? FGInputTypeEnum.Text;
  const type = incomingConfigModuleValue?.fieldValue.smartField ? 'SMART_FIELD' : defaultType;
  return {
    initial: {
      value: transformTemplateValues(incomingConfigModuleValue, fieldAttributes),
      type,
      templateDefault: extractDefaultValueFromTemplate(fieldAttributes),
      smartFieldDefault: type === 'SMART_FIELD' ? incomingConfigModuleValue?.fieldValue?.single : undefined,
    },
    dirty: dirtyFieldValue
      ? {
          value: dirtyFieldValue?.smartField ?? dirtyFieldValue?.value,
          type: dirtyFieldValue?.smartField ? 'SMART_FIELD' : defaultType,
        }
      : undefined,
    isRequired: !!fieldAttributes?.isRequired,
  };
}

function getTagFieldMappingValue(
  templateAttributes: Record<string, ParameterModuleTemplateDetailedResourceParameterField>,
  templateValuesMapping?: Record<string, TemplateValues[number]['values'][number]>,
  activeDirtyParameters?: DirtyParameterModuleFieldMapping,
): ParameterModuleFieldMapping {
  return Object.keys(templateAttributes).reduce(
    (accum, fieldId) => ({
      ...accum,
      [fieldId]: getValuesFromTemplates(
        templateAttributes?.[fieldId],
        templateValuesMapping?.[fieldId],
        activeDirtyParameters?.[fieldId],
      ),
    }),
    {} as ParameterModuleFieldMapping,
  );
}

export function reduceAllFieldValues(
  templateValues?: TemplateValues,
): Record<string, Record<string, TemplateValues[number]['values'][number]>> {
  return toArray(templateValues).reduce(
    (accum, current) => ({
      ...accum,
      [current.template.id]: current.values.reduce(
        (accumValues, value) => ({
          ...accumValues,
          [value.fieldId]: value,
        }),
        {} as Record<string, TemplateValues[number]['values'][number]>,
      ),
    }),
    {} as Record<string, Record<string, TemplateValues[number]['values'][number]>>,
  );
}

function getTemplateFieldsFromContainerMapping(
  containersFields?: Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>,
): Record<string, ParameterModuleTemplateDetailedResourceParameterField> {
  return Object.keys(containersFields ?? {}).reduce(
    (accum, containerId) => ({
      ...accum,
      ...containersFields?.[containerId],
    }),
    {},
  );
}

function reduceAllFields(
  fieldsMapping: Record<
    ParameterModuleTemplateFieldId,
    Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>
  >,
): Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>> {
  return Object.keys(fieldsMapping).reduce(
    (accum, templateId) => ({
      ...accum,
      [templateId]: getTemplateFieldsFromContainerMapping(fieldsMapping?.[templateId]),
    }),
    {} as Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>,
  );
}

export function createFieldsValuesMapping(
  fieldsMapping: Record<
    ParameterModuleTemplateFieldId,
    Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>
  >,
  templateValues?: TemplateValues,
  dirtyParameters?: DirtyParameterModuleContentFieldMapping,
): ParameterModuleContentFieldMapping {
  const allFieldValuesMapping = reduceAllFieldValues(templateValues);
  const allFieldsMapping = reduceAllFields(fieldsMapping);

  return (
    Object.keys(fieldsMapping)?.reduce(
      (accum, templateId) => ({
        ...accum,
        [templateId]: getTagFieldMappingValue(
          allFieldsMapping[templateId],
          allFieldValuesMapping?.[templateId],
          dirtyParameters?.[templateId],
        ),
      }),
      {} as ParameterModuleContentFieldMapping,
    ) ?? {}
  );
}

export function getTemplateValuesFromConfigurationModule(data: ConfigurationModule | null | undefined): TemplateValues {
  return (
    data?.parameterSets.map((parameterSet) => {
      return {
        template: {
          id: parameterSet.templateId,
        },
        values: parameterSet.values,
      };
    }) ?? []
  );
}

function getTableFieldActiveValue(
  moduleDetailsFieldMappingAttributes: ParameterModuleFieldMappingAttributes,
  tableKey: string,
): FormGeneratorTableValue | undefined {
  const tableValue = (moduleDetailsFieldMappingAttributes?.dirty?.value ??
    moduleDetailsFieldMappingAttributes?.initial?.value) as FormGeneratorTableValue | undefined;

  return checkIsTableFieldValue(tableValue, tableKey) ? tableValue : undefined;
}

function transformSetFieldIntoSelectOption({
  selectedTemplateValues,
  dynamicOptionsSource,
  dynamicOptionLabelSource,
  dynamicOptionValueSource,
}: {
  readonly selectedTemplateValues: ParameterModuleFieldMapping;
  readonly dynamicOptionsSource: string;
  readonly dynamicOptionLabelSource?: string;
  readonly dynamicOptionValueSource?: string;
}): FGInputSelectModel['inputOptions'] {
  const currentDependencyFieldValue = getTableFieldActiveValue(
    selectedTemplateValues[dynamicOptionsSource],
    dynamicOptionsSource,
  );

  const { rows = {} } = currentDependencyFieldValue
    ? (currentDependencyFieldValue as unknown as FormGeneratorTableValue)[dynamicOptionsSource]
    : {};
  return isEmpty(rows)
    ? []
    : Object.values(rows).map((row, index) => ({
        label: toString(row[toString(dynamicOptionLabelSource)]),
        value: toString(row[toString(dynamicOptionValueSource)]),
        id: index,
        order: index,
      }));
}

export function getSelectInputOptionsFromParametersSet(
  inputSelectModel: FGInputSelectModel,
  selectedTemplateValues = {} as ParameterModuleFieldMapping,
): FGInputSelectModel['inputOptions'] {
  return inputSelectModel.dynamicOptionsSource
    ? transformSetFieldIntoSelectOption({
        selectedTemplateValues,
        dynamicOptionsSource: inputSelectModel.dynamicOptionsSource,
        dynamicOptionLabelSource: inputSelectModel.dynamicOptionLabelSource,
        dynamicOptionValueSource: inputSelectModel.dynamicOptionValueSource,
      })
    : inputSelectModel.inputOptions;
}

export function injectSelectFieldDynamicOption(
  input: FGInputModel,
  selectedTemplateValues = {} as ParameterModuleFieldMapping,
): FGInputModel {
  return {
    ...input,
    inputCustomConfig: {
      ...input.inputCustomConfig,
      inputOptions: getSelectInputOptionsFromParametersSet(
        input.inputCustomConfig as FGInputSelectModel,
        selectedTemplateValues,
      ),
    } as FGInputSelectModel,
  };
}

export function lookUpAndChangeDynamicOptionFields(
  inputSchema: FGInputTableModel<FGInputModel>['inputsSchema'],
  selectedTemplateValues = {} as ParameterModuleFieldMapping,
): FGInputTableModel<FGInputModel>['inputsSchema'] {
  return inputSchema.map((field) => {
    switch (field.inputCustomConfig.type) {
      case FGInputTypeEnum.Table: {
        return {
          ...field,
          inputCustomConfig: {
            ...field.inputCustomConfig,
            inputsSchema: lookUpAndChangeDynamicOptionFields(
              field.inputCustomConfig.inputsSchema,
              selectedTemplateValues,
            ),
          },
        };
      }
      case FGInputTypeEnum.MultiSelect:
      case FGInputTypeEnum.Select: {
        return injectSelectFieldDynamicOption(field, selectedTemplateValues);
      }
      default: {
        return field;
      }
    }
  });
}

export function injectTableFieldDynamicOption(
  input: FGInputModel,
  selectedTemplateValues = {} as ParameterModuleFieldMapping,
): FGInputModel {
  return {
    ...input,
    inputCustomConfig: {
      ...input.inputCustomConfig,
      inputsSchema: lookUpAndChangeDynamicOptionFields(
        (input.inputCustomConfig as FGInputTableModel<FGInputModel>).inputsSchema,
        selectedTemplateValues,
      ),
    } as FGInputTableModel<FGInputModel>,
  };
}

export function computeDynamicInputOption(
  input: FGInputModel,
  selectedTemplateValues = {} as ParameterModuleFieldMapping,
): FGInputModel {
  switch (input.inputCustomConfig.type) {
    case FGInputTypeEnum.MultiSelect:
    case FGInputTypeEnum.Select: {
      return injectSelectFieldDynamicOption(input, selectedTemplateValues);
    }
    case FGInputTypeEnum.Table: {
      return injectTableFieldDynamicOption(input, selectedTemplateValues);
    }
    default: {
      return input;
    }
  }
}

export function computeDynamicFieldsOptions(
  formBuilderConfig: FGBuildModel[],
  selectedTemplateValues = {} as ParameterModuleFieldMapping,
): FGBuildModel[] {
  return formBuilderConfig.map((formConfig) => ({
    ...formConfig,
    sections: formConfig.sections.map((section) => ({
      ...section,
      inputs: section.inputs.map((input) => computeDynamicInputOption(input, selectedTemplateValues)),
    })),
  }));
}

export function transformTemplatesForFormGeneratorWithCategories({
  containers,
  fieldsMapping,
  valuesMapping,
  locale = FGLanguagesEnum.Us,
  disabled = false,
  selectedTemplateId = '',
  smartFields,
  smartFieldPrefix,
}: {
  readonly containers?: TemplateDetailedResourceParameterContainer[];
  readonly fieldsMapping: Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>;
  readonly valuesMapping?: ParameterModuleContentFieldMapping;
  readonly locale?: FGLanguagesEnum;
  readonly disabled?: boolean;
  readonly selectedTemplateId?: string;
  readonly smartFields: Record<string, Record<string, SmartFieldsResource>>;
  readonly smartFieldPrefix: string;
}): FGBuildModel[] {
  const selectedTemplateValues = valuesMapping?.[selectedTemplateId];

  const formBuilderConfig =
    selectedTemplateId === ''
      ? []
      : toArray(
          containers?.map(({ name, id }) => ({
            title: name,
            inputLanguage: locale,
            outputLanguage: locale,
            sections: getSectionFieldsFromMapping({
              id,
              fieldsMapping,
              smartFields: smartFields[selectedTemplateId],
              valuesMapping: selectedTemplateValues,
              disabled,
              smartFieldPrefix,
            }),
          })),
        );

  return formBuilderConfig.length > 0
    ? computeDynamicFieldsOptions(formBuilderConfig, selectedTemplateValues)
    : formBuilderConfig;
}
export function transformTemplatesForFormGeneratorWithCategoriesForVirtualDevice({
  containers,
  fieldsMapping,
  valuesMapping,
  locale = FGLanguagesEnum.Us,
  disabled = false,
  selectedTemplateId = '',
  smartFields,
  smartFieldPrefix,
}: {
  readonly containers?: TemplateDetailedResourceParameterContainer[];
  readonly fieldsMapping: Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>;
  readonly valuesMapping?: ParameterModuleContentFieldMapping;
  readonly locale?: FGLanguagesEnum;
  readonly disabled?: boolean;
  readonly selectedTemplateId?: string;
  readonly smartFields: Record<string, Record<string, SmartFieldsResource>>;
  readonly smartFieldPrefix: string;
}): FGBuildModel[] {
  const selectedTemplateValues = valuesMapping?.[selectedTemplateId];

  const formBuilderConfig =
    selectedTemplateId === ''
      ? []
      : toArray(
          containers?.map(({ name, id }) => ({
            title: name,
            inputLanguage: locale,
            outputLanguage: locale,
            sections: getSectionFieldsFromMappingForVirtualDevice({
              id,
              fieldsMapping,
              smartFields: smartFields[selectedTemplateId],
              valuesMapping: selectedTemplateValues,
              disabled,
              smartFieldPrefix,
            }),
          })),
        );

  return formBuilderConfig.length > 0
    ? computeDynamicFieldsOptions(formBuilderConfig, selectedTemplateValues)
    : formBuilderConfig;
}

export const initialPreviewModalState: PreviewModalState = { isOpen: false, mode: 'view' };

function buildValuesMappingFromFormState([form]: FGOutputModel<FGBuildModel>): Record<string, FGInputsType> {
  return form.inputs.reduce(
    (accum, current) => ({ ...accum, [current.inputKey]: current.value }),
    {} as Record<string, FGInputsType>,
  );
}

function manageSmartFieldChanges(
  fieldKey: string,
  incomingFormValuesMapping?: FGInputsType,
  currentParametersValues?: Partial<ParameterModuleFieldMappingAttributes>,
): DirtyParameterModuleFieldMapping {
  if (currentParametersValues?.initial?.type === 'SMART_FIELD' && currentParametersValues?.dirty) {
    return {
      [fieldKey]: {
        type: 'value',
        value: incomingFormValuesMapping,
      },
    };
  } else if (
    currentParametersValues?.initial?.type !== 'SMART_FIELD' &&
    currentParametersValues?.dirty?.type === 'SMART_FIELD'
  ) {
    return {
      [fieldKey]: {
        type: 'smartField',
        value: incomingFormValuesMapping,
      },
    };
  } else {
    return {};
  }
}

function checkChangedValueByFieldType(
  initial?: ParameterModuleFieldMappingState,
  incomingFormValuesMapping?: FGInputsType,
): boolean {
  switch (initial?.type) {
    case FGInputTypeEnum.Number: {
      return isEqual(
        toNumber(initial?.value ?? Number.NEGATIVE_INFINITY),
        toNumber(incomingFormValuesMapping ?? Number.NEGATIVE_INFINITY),
      );
    }
    case FGInputTypeEnum.MultiSelect: {
      return isEqual(initial?.value, incomingFormValuesMapping);
    }
    default: {
      const initialValue = initial?.value === '' ? undefined : (initial?.value ?? undefined);
      const changedValue = incomingFormValuesMapping === '' ? undefined : (incomingFormValuesMapping ?? undefined);
      return isEqual(initialValue, changedValue);
    }
  }
}

function manageCommonFieldChange(
  fieldKey: string,
  incomingFormValuesMapping?: FGInputsType,
  currentParametersValues?: Partial<ParameterModuleFieldMappingAttributes>,
): DirtyParameterModuleFieldMapping {
  return checkChangedValueByFieldType(currentParametersValues?.initial, incomingFormValuesMapping)
    ? {}
    : {
        [fieldKey]: {
          type: 'value',
          value: incomingFormValuesMapping,
        },
      };
}

function updateChangedParameterValue(
  incomingFormValuesMapping: Record<string, FGInputsType>,
  currentParametersValuesMapping?: Record<string, Partial<ParameterModuleFieldMappingAttributes>>,
): DirtyParameterModuleFieldMapping {
  return Object.keys(incomingFormValuesMapping ?? {}).reduce((accum, current) => {
    const isInitiallyChangedSmartField =
      currentParametersValuesMapping?.[current]?.initial?.type === 'SMART_FIELD' &&
      currentParametersValuesMapping?.[current]?.dirty;

    const isDirtySmartField = currentParametersValuesMapping?.[current]?.dirty?.type === 'SMART_FIELD';

    const isSmartFieldChange = isInitiallyChangedSmartField || isDirtySmartField;

    const isPrestineSmartField =
      currentParametersValuesMapping?.[current]?.initial?.type === 'SMART_FIELD' &&
      !currentParametersValuesMapping?.[current]?.dirty;

    if (isPrestineSmartField) {
      return accum;
    }

    return {
      ...accum,
      ...(isSmartFieldChange
        ? manageSmartFieldChanges(
            current,
            incomingFormValuesMapping?.[current],
            currentParametersValuesMapping?.[current],
          )
        : manageCommonFieldChange(
            current,
            incomingFormValuesMapping?.[current],
            currentParametersValuesMapping?.[current],
          )),
    };
  }, {} as DirtyParameterModuleFieldMapping);
}

function extractCurrentFormValuesFromInitialValuesMapping(
  incomingFormValuesMapping: Record<string, FGInputsType>,
  valuesMapping?: ParameterModuleFieldMapping,
): Record<string, Partial<ParameterModuleFieldMappingAttributes>> {
  return Object.keys(incomingFormValuesMapping).reduce(
    (accum, current) => ({
      ...accum,
      [current]: {
        initial: valuesMapping?.[current]?.initial,
        dirty: valuesMapping?.[current]?.dirty,
      },
    }),
    {} as Record<string, Partial<ParameterModuleFieldMappingAttributes>>,
  );
}

function removeActiveFormValuesFromDirties(
  currentDirtyState: DirtyParameterModuleFieldMapping,
  activeFormKeys: string[],
): DirtyParameterModuleFieldMapping {
  return omit(currentDirtyState, activeFormKeys);
}

function getSmartFieldsFromActiveParameter(
  currentDirtyState?: DirtyParameterModuleFieldMapping,
  valuesMapping?: ParameterModuleFieldMapping,
): DirtyParameterModuleFieldMapping {
  return Object.keys(valuesMapping ?? {}).reduce((accum, fieldKey) => {
    return {
      ...accum,
      ...(currentDirtyState?.[fieldKey]?.type === 'smartField' ? { [fieldKey]: currentDirtyState?.[fieldKey] } : {}),
    };
  }, {} as DirtyParameterModuleFieldMapping);
}

function manageChangedParameters(
  selectedParameterId: string,
  currentDirtyState: DirtyParameterModuleContentFieldMapping,
  incomingFormValuesMapping: Record<string, FGInputsType>,
  valuesMapping?: ParameterModuleContentFieldMapping,
): DirtyParameterModuleContentFieldMapping {
  const currentFormInitialValues = extractCurrentFormValuesFromInitialValuesMapping(
    incomingFormValuesMapping,
    valuesMapping?.[selectedParameterId],
  );

  const updatedParametersValues = updateChangedParameterValue(incomingFormValuesMapping, currentFormInitialValues);

  const hasAnyModification = Object.keys(updatedParametersValues).length > 0;

  const dirty = currentDirtyState?.[selectedParameterId] ?? {};

  const remaningDirtyTemplateValues = removeActiveFormValuesFromDirties(dirty, Object.keys(incomingFormValuesMapping));
  const hasExistingModifications = Object.keys(dirty).length > 0;

  const remaningSmartFields = getSmartFieldsFromActiveParameter(
    currentDirtyState?.[selectedParameterId],
    valuesMapping?.[selectedParameterId],
  );

  const hasRemaningSmartFields = Object.keys(remaningSmartFields).length > 0;

  return hasAnyModification || hasExistingModifications || hasRemaningSmartFields
    ? { [selectedParameterId]: { ...remaningDirtyTemplateValues, ...updatedParametersValues, ...remaningSmartFields } }
    : {};
}

export function mergeDirtyFields(
  selectedParameterId: string,
  formState: FGOutputModel<FGBuildModel>,
  currentDirtyState: DirtyParameterModuleContentFieldMapping,
  valuesMapping?: ParameterModuleContentFieldMapping,
): DirtyParameterModuleContentFieldMapping {
  const incomingFormValuesMapping = buildValuesMappingFromFormState(formState);

  const result = {
    ...currentDirtyState,
    ...manageChangedParameters(selectedParameterId, currentDirtyState, incomingFormValuesMapping, valuesMapping),
  };
  return isEqual(result, currentDirtyState) ? currentDirtyState : result;
}

export function createReviewSections(
  dirtyState: DirtyParameterModuleContentFieldMapping,
  data?: TemplateListResource[],
): ParameterModuleContentParametersModalSection[] {
  const parametersLabelsMapping = data?.reduce(
    (accum, current) => ({ ...accum, [current.id]: toString(current.name) }),
    {} as Record<string, string>,
  );
  return Object.keys(dirtyState).map((parameterId) => ({
    title: toString(parametersLabelsMapping?.[parameterId]),
    id: parameterId,
    count: Object.keys(dirtyState?.[parameterId] ?? {}).length,
  }));
}

export function countChanges(dirtyState: DirtyParameterModuleContentFieldMapping): number {
  return Object.keys(dirtyState).reduce((accum, current) => (accum += Object.keys(dirtyState[current]).length), 0);
}

function extractFGValueForParameterSet(
  value?: Exclude<FGInputsType, FormGeneratorTableValue>,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number]['fieldValue']['single'] {
  return (
    value === undefined ? value : parseSingleFieldValue(value)
  ) as UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number]['fieldValue']['single'];
}

function getMappingSingleFieldValue(
  mappingValue: ParameterModuleFieldMappingState,
  valueSourceKey = 'value' as Extract<keyof ParameterModuleFieldMappingState, 'templateDefault' | 'value'>,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number]['fieldValue']['single'] {
  return (
    extractFGValueForParameterSet(mappingValue?.[valueSourceKey] as Exclude<FGInputsType, FormGeneratorTableValue>) ??
    undefined
  );
}

function createSingleParameterSet(
  fieldId: string,
  value?: string,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number] {
  return {
    fieldId,
    fieldValue: {
      type: 'SINGLE',
      single: value,
    },
  };
}

function createSmartFieldParameterSet(
  fieldId: string,
  smartFieldsResource: SmartFieldsResource,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number] {
  return {
    fieldId,
    fieldValue: {
      type: 'SINGLE',
      smartField: {
        id: toString(smartFieldsResource.id),
      },
    },
  };
}

function getSingleParameterSetFieldValue(
  fieldId: string,
  mappingValue: ParameterModuleFieldMappingState,
  valueSourceKey = 'value' as Extract<keyof ParameterModuleFieldMappingState, 'templateDefault' | 'value'>,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number] {
  return mappingValue.type === 'SMART_FIELD'
    ? createSmartFieldParameterSet(fieldId, mappingValue?.[valueSourceKey] as SmartFieldsResource)
    : createSingleParameterSet(fieldId, getMappingSingleFieldValue(mappingValue, valueSourceKey));
}

function getMappingStructureFieldValue(
  fieldId: string,
  mappingValue: ParameterModuleFieldMappingState,
  valueSourceKey = 'value' as Extract<keyof ParameterModuleFieldMappingState, 'templateDefault' | 'value'>,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number]['fieldValue']['structure'] {
  return (
    extractFGTableValueForParameterSet(fieldId, mappingValue?.[valueSourceKey] as FormGeneratorTableValue) ?? undefined
  );
}

function getStructureParameterSetFieldValue(
  fieldId: string,
  mappingValue: ParameterModuleFieldMappingState,
  valueSourceKey = 'value' as Extract<keyof ParameterModuleFieldMappingState, 'templateDefault' | 'value'>,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number] {
  return {
    fieldId,
    fieldValue: {
      type: 'STRUCTURE',
      structure: getMappingStructureFieldValue(fieldId, mappingValue, valueSourceKey)?.filter((field) => field),
    },
  };
}

function getInialTemplateValueAsParameterSet(
  fieldId: string,
  moduleDetailsFieldMappingState?: ParameterModuleFieldMappingState,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number] | undefined {
  return moduleDetailsFieldMappingState &&
    moduleDetailsFieldMappingState.type !== FGInputTypeEnum.Table &&
    moduleDetailsFieldMappingState?.templateDefault !== undefined
    ? (getSingleParameterSetFieldValue(fieldId, moduleDetailsFieldMappingState, 'templateDefault') ?? undefined)
    : undefined;
}

function transformMappingValue(
  fieldId: string,
  moduleDetailsFieldMappingAttributes: ParameterModuleFieldMappingAttributes,
  configurationModuleParameterSetValue?: ConfigurationModuleParameterSetValue,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number] | undefined {
  if (moduleDetailsFieldMappingAttributes?.dirty) {
    const mappingValue = moduleDetailsFieldMappingAttributes?.dirty ?? moduleDetailsFieldMappingAttributes?.initial;
    switch (mappingValue.type) {
      case FGInputTypeEnum.Table: {
        return getStructureParameterSetFieldValue(fieldId, mappingValue);
      }
      default: {
        return getSingleParameterSetFieldValue(fieldId, mappingValue);
      }
    }
  } else {
    return (
      configurationModuleParameterSetValue ??
      getInialTemplateValueAsParameterSet(fieldId, moduleDetailsFieldMappingAttributes?.initial) ??
      undefined
    );
  }
}

function transformSmartField(
  fieldId: string,
  smartField: SmartFieldsResource,
  moduleDetailsFieldMappingAttributes?: ParameterModuleFieldMappingAttributes,
  initialFieldMapping?: ConfigurationModuleParameterSetValue,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'][number] | undefined {
  return !!initialFieldMapping || moduleDetailsFieldMappingAttributes?.dirty
    ? {
        fieldId,
        fieldValue: {
          type: 'SMART_FIELD',
          smartField: {
            id: toString(
              (moduleDetailsFieldMappingAttributes?.dirty?.value as SmartFieldsResource)?.id ?? smartField.id,
            ),
          },
        },
      }
    : undefined;
}

export function transformMappingValuesIntoParametersSetValues(
  templateValuesMapping: ParameterModuleFieldMapping,
  templateSmartFields: Record<string, SmartFieldsResource>,
  initialFieldsMapping?: Record<string, ConfigurationModuleParameterSetValue>,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'] {
  return Object.keys(templateValuesMapping)
    .map((mappingFieldId) => {
      return templateSmartFields[mappingFieldId]
        ? transformSmartField(
            mappingFieldId,
            templateSmartFields[mappingFieldId],
            templateValuesMapping?.[mappingFieldId],
            initialFieldsMapping?.[mappingFieldId],
          )
        : transformMappingValue(
            mappingFieldId,
            templateValuesMapping[mappingFieldId],
            initialFieldsMapping?.[mappingFieldId],
          );
    })
    .filter((field) => !!field);
}

export function mergeConfigModuleTemplateValues(
  initial: UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'],
  modifieds: UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'],
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'][number]['values'] {
  const initialsWithoutModifieds = initial.filter(
    ({ fieldId }) => !modifieds.some(({ fieldId: modifiedFieldId }) => modifiedFieldId === fieldId),
  );
  return [...modifieds, ...initialsWithoutModifieds];
}

function mergeInitialAndCurrentConfigModuleValues(
  initialConfigModuleValues: UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'],
  modifiedTemplatesResults: UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'],
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'] {
  const uniqueTemplateIds = [
    ...new Set([
      ...initialConfigModuleValues.map(({ templateId }) => templateId),
      ...modifiedTemplatesResults.map(({ templateId }) => templateId),
    ]),
  ];
  return uniqueTemplateIds.reduce(
    (accum, templateId) => {
      const modifiedFields = modifiedTemplatesResults.find(
        ({ templateId: modifiedTempalateId }) => modifiedTempalateId === templateId,
      )?.values;
      const initialValues = initialConfigModuleValues.find(
        ({ templateId: initialTempalateId }) => initialTempalateId === templateId,
      )?.values;

      const hasAnyModification = !!modifiedFields?.length;
      return hasAnyModification || initialValues
        ? [
            ...accum,
            {
              templateId,
              values: mergeConfigModuleTemplateValues(toArray(initialValues), toArray(modifiedFields)),
            },
          ]
        : accum;
    },
    [] as UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'],
  );
}

export function transformValuesMappingIntoParametersSet(
  valuesMapping: ParameterModuleContentFieldMapping,
  allSmartFields: Record<string, Record<string, SmartFieldsResource>>,
  initialConfigModulesValues?: TemplateValues,
): UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'] {
  const initialFieldsMapping = reduceAllFieldValues(initialConfigModulesValues);
  const modifiedTemplatesResults: UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'] = Object.keys(
    valuesMapping,
  ).map((templateId) => {
    const newModifiedFields: ConfigurationModuleParameterSetValue[] = transformMappingValuesIntoParametersSetValues(
      valuesMapping[templateId],
      allSmartFields[templateId],
      initialFieldsMapping[templateId],
    );

    return {
      templateId,
      values: newModifiedFields,
    };
  });

  const configModuleValues: UpdateConfigurationModuleByIdV2MutationRequestApi['parameterSets'] =
    initialConfigModulesValues?.map(({ template }) => {
      const valuesWithChanges = Object.values(initialFieldsMapping[template.id]);

      return {
        templateId: template.id,
        values: valuesWithChanges,
      };
    }) ?? [];

  return mergeInitialAndCurrentConfigModuleValues(configModuleValues, modifiedTemplatesResults);
}

export function setSmartFieldIntoDirtyState(
  currentDirtyState: DirtyParameterModuleContentFieldMapping,
  activeParameterId: string,
  fieldKey: string,
  smartField: SmartFieldsResource,
): DirtyParameterModuleContentFieldMapping {
  return {
    ...currentDirtyState,
    [activeParameterId]: {
      ...currentDirtyState[activeParameterId],
      [fieldKey]: {
        type: 'smartField',
        smartField,
      },
    },
  };
}

export function unmapSmartFieldFromDirtyState(
  currentDirtyState: DirtyParameterModuleContentFieldMapping,
  activeParameterId: string,
  fieldKey: string,
  fieldsMapping: Record<
    ParameterModuleTemplateFieldId,
    Record<string, Record<string, ParameterModuleTemplateDetailedResourceParameterField>>
  >,
  templateValues?: TemplateValues,
): DirtyParameterModuleContentFieldMapping {
  const smartFieldAlreadyMapped = get(cloneDeep(currentDirtyState), `${activeParameterId}.${fieldKey}`);

  const allFieldValuesMapping = reduceAllFieldValues(templateValues);
  const allFieldsMapping = reduceAllFields(fieldsMapping);

  const initialValue = manageIncomingFieldValues(
    allFieldValuesMapping?.[activeParameterId]?.[fieldKey],
    allFieldsMapping?.[activeParameterId]?.[fieldKey],
  );

  return smartFieldAlreadyMapped
    ? omit(cloneDeep(currentDirtyState), `${activeParameterId}.${fieldKey}`)
    : {
        ...currentDirtyState,
        [activeParameterId]: {
          ...currentDirtyState[activeParameterId],
          [fieldKey]: {
            type: 'value',
            value: initialValue,
          },
        },
      };
}

function getActiveTemplateSmartFields(
  dirtyFieldsMapping: DirtyParameterModuleFieldMapping,
): Record<string, SmartFieldsResource> {
  return Object.keys(dirtyFieldsMapping).reduce(
    (accum, fieldKey) => ({
      ...accum,
      ...(dirtyFieldsMapping[fieldKey].type === 'smartField' && dirtyFieldsMapping[fieldKey].smartField
        ? { [fieldKey]: dirtyFieldsMapping[fieldKey].smartField }
        : {}),
    }),
    {} as Record<string, SmartFieldsResource>,
  );
}

export function getAllSmartFieldsFromDirtyState(
  currentSmartFields: Record<string, Record<string, SmartFieldsResource>>,
  currentDirtyState: DirtyParameterModuleContentFieldMapping,
): Record<string, Record<string, SmartFieldsResource>> {
  const dirtySmartFields = Object.keys(currentDirtyState).reduce(
    (accum, templateId) => ({
      ...accum,
      [templateId]: {
        ...accum,
        ...getActiveTemplateSmartFields(currentDirtyState[templateId]),
      },
    }),
    {} as Record<string, Record<string, SmartFieldsResource>>,
  );
  return Object.keys(currentSmartFields).reduce(
    (accum, templateId) => ({
      ...accum,
      [templateId]: dirtySmartFields[templateId] ?? currentSmartFields[templateId],
    }),
    {},
  );
}

function checkIsInputKey(
  inputKey: string,
  field: TemplateDetailedResourceParameterField,
): EnumSmartFieldDataType | undefined {
  if (field.id === inputKey) {
    return field.definition?.dynamicChoice ? 'STRING' : field.dataType;
  } else {
    return field.dataType === 'STRUCTURE'
      ? getInputDataTypeByKey(inputKey, field.definition.structure?.childFields)
      : undefined;
  }
}

export function getInputDataTypeByKey(
  inputKey: string,
  fields?: TemplateDetailedResourceParameterField[],
): EnumSmartFieldDataType | undefined {
  return fields?.reduce(
    (found, current) => found ?? checkIsInputKey(inputKey, current),
    undefined as EnumSmartFieldDataType | undefined,
  );
}

function getDisplayedSmartFieldComponent(
  fieldId: string,
  moduleDetailsFieldMapping: ParameterModuleFieldMapping,
): Record<string, SmartFieldsResource> {
  if (moduleDetailsFieldMapping[fieldId]?.dirty) {
    return moduleDetailsFieldMapping[fieldId]?.dirty?.type === 'SMART_FIELD'
      ? {
          [fieldId]: moduleDetailsFieldMapping[fieldId]?.dirty?.value as SmartFieldsResource,
        }
      : {};
  } else {
    return moduleDetailsFieldMapping[fieldId]?.initial?.type === 'SMART_FIELD'
      ? {
          [fieldId]: moduleDetailsFieldMapping[fieldId]?.initial?.value as SmartFieldsResource,
        }
      : {};
  }
}

function checkModuleDetailsFieldMappingIsSmartField(
  moduleDetailsFieldMappingAttributes: ParameterModuleFieldMappingAttributes,
): boolean {
  return Boolean(
    moduleDetailsFieldMappingAttributes?.dirty?.type === 'SMART_FIELD' ||
      (moduleDetailsFieldMappingAttributes?.initial?.type === 'SMART_FIELD' &&
        !moduleDetailsFieldMappingAttributes?.dirty),
  );
}

function filterSmartFieldsFromValuesMapping(
  moduleDetailsFieldMapping: ParameterModuleFieldMapping,
): Record<string, SmartFieldsResource> {
  return Object.keys(moduleDetailsFieldMapping).reduce(
    (accum, fieldId) => {
      return {
        ...accum,
        ...(checkModuleDetailsFieldMappingIsSmartField(moduleDetailsFieldMapping[fieldId])
          ? getDisplayedSmartFieldComponent(fieldId, moduleDetailsFieldMapping)
          : {}),
      };
    },
    {} as Record<string, SmartFieldsResource>,
  );
}

export function getAllSmartFieldsFromDirtyStateMappingValue(
  valuesMapping: ParameterModuleContentFieldMapping,
): Record<string, Record<string, SmartFieldsResource>> {
  return Object.keys(valuesMapping).reduce(
    (accum, templateId) => ({
      ...accum,
      [templateId]: filterSmartFieldsFromValuesMapping(valuesMapping[templateId]),
    }),
    {} as Record<string, Record<string, SmartFieldsResource>>,
  );
}

export function getOnlyTemplatesInErrorState(
  currentSelectedTemplate: string,
  currentErrorsState: Record<string, boolean>,
  isValid: boolean,
): Record<string, boolean> {
  return isValid
    ? omit(cloneDeep(currentErrorsState), currentSelectedTemplate)
    : set(cloneDeep(currentErrorsState), currentSelectedTemplate, !isValid);
}

export function createStagedTemplatesMapping(
  current: Record<ParameterModuleTemplateFieldId, TemplateDetailedResource>,
  newRetrievedTemplateDetailedResource?: TemplateDetailedResource,
): Record<ParameterModuleTemplateFieldId, TemplateDetailedResource> {
  return {
    ...current,
    ...(newRetrievedTemplateDetailedResource
      ? { [newRetrievedTemplateDetailedResource.id]: newRetrievedTemplateDetailedResource }
      : {}),
  };
}

export function checkShouldDisplayKebabMenuLeftIcon(
  templatesInErrorState: Record<string, boolean>,
  activeKey: string,
): boolean {
  return Object.keys(templatesInErrorState).some(
    (templateId) => templatesInErrorState[templateId] && templateId !== activeKey,
  );
}

function extractDependantFieldValue({
  row,
  dynamicOptionValueSource,
  inputKey,
}: {
  readonly row: ObjectRecord;
  readonly dynamicOptionValueSource: string;
  readonly inputKey: string;
}): DependantFieldsInfo[] {
  return row[inputKey]
    ? [
        {
          value: toString(row[inputKey]),
          dynamicOptionValueSource,
        },
      ]
    : [];
}

function transformToDependantStructure({
  value,
  dynamicOptionValueSource,
  inputKey,
}: {
  readonly value: FormGeneratorTableValue;
  readonly dynamicOptionValueSource: string;
  readonly inputKey: string;
}): SelectedDependantFields {
  const [key] = Object.keys(value);
  return {
    [key]: Object.values(value[key].rows).reduce(
      (accum, row) => [...accum, ...extractDependantFieldValue({ row, dynamicOptionValueSource, inputKey })],
      [] as DependantFieldsInfo[],
    ),
  };
}

function getAccumRow({
  row,
  dynamicOptionsSource,
  dynamicOptionValueSource,
  inputKey,
}: {
  readonly row: ObjectRecord;
  readonly dynamicOptionsSource: string;
  readonly dynamicOptionValueSource: string;
  readonly inputKey: string;
}): SelectedDependantFields {
  return Object.keys(row).reduce((accumAttribute, rowKey) => {
    const isBuggyRow = checkIsTableFieldValue(
      { [rowKey]: (row[rowKey] as unknown as FormGeneratorTableValue)?.[rowKey] },
      rowKey,
    );
    const buggyValue = (
      isBuggyRow ? { [rowKey]: (row[rowKey] as unknown as FormGeneratorTableValue)?.[rowKey] } : {}
    ) as FormGeneratorTableValue;

    const value = isBuggyRow ? buggyValue : ({ [rowKey]: row[rowKey] } as FormGeneratorTableValue);

    return checkIsTableFieldValue(value, rowKey)
      ? findNestedTableValueByKey(value, dynamicOptionsSource, dynamicOptionValueSource, inputKey)
      : accumAttribute;
  }, {} as SelectedDependantFields);
}

function findValueInRows({
  rows,
  dynamicOptionsSource,
  dynamicOptionValueSource,
  inputKey,
}: {
  readonly rows: Record<string, ObjectRecord>;
  readonly dynamicOptionsSource: string;
  readonly dynamicOptionValueSource: string;
  readonly inputKey: string;
}): SelectedDependantFields {
  return Object.values(rows).reduce((accumRow: SelectedDependantFields, row: ObjectRecord) => {
    return mergeSelectedDependantFields(
      accumRow,
      getAccumRow({ row, dynamicOptionsSource, dynamicOptionValueSource, inputKey }),
    );
  }, {} as SelectedDependantFields);
}

function findNestedTableValueByKey(
  tableFieldValue: FormGeneratorTableValue,
  dynamicOptionsSource: string,
  dynamicOptionValueSource: string,
  inputKey: string,
): SelectedDependantFields {
  if (tableFieldValue?.[dynamicOptionsSource]) {
    return transformToDependantStructure({ value: tableFieldValue, dynamicOptionValueSource, inputKey });
  }

  return Object.entries(tableFieldValue ?? {}).reduce((accum, [_, current]) => {
    return mergeSelectedDependantFields(
      accum,
      findValueInRows({ rows: current.rows, dynamicOptionsSource, dynamicOptionValueSource, inputKey }),
    );
  }, {} as SelectedDependantFields);
}

function findNestedTableValues({
  rootValue,
  dynamicOptionsSource,
  dynamicOptionValueSource,
  inputKey,
}: {
  readonly rootValue: FormGeneratorTableValue;
  readonly dynamicOptionsSource: string;
  readonly dynamicOptionValueSource: string;
  readonly inputKey: string;
}): SelectedDependantFields {
  return findNestedTableValueByKey(rootValue, dynamicOptionsSource, dynamicOptionValueSource, inputKey);
}

function getSelectedTableValuesFromParameters({
  moduleDetailsFieldMapping,
  rootValue,
  input,
  parent,
}: {
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
  readonly rootValue: FGInputModel;
  readonly input: FGInputModel;
  readonly parent: FGInputModel;
}): SelectedDependantFields {
  const currentRootValue =
    getTableFieldActiveValue(moduleDetailsFieldMapping[rootValue.inputKey], rootValue.inputKey) ?? {};
  const dynamicOptionValueSource = toString((input.inputCustomConfig as FGInputSelectModel).dynamicOptionValueSource);
  return findNestedTableValues({
    rootValue: currentRootValue,
    dynamicOptionsSource: parent.inputKey,
    dynamicOptionValueSource,
    inputKey: input.inputKey,
  });
}

function checkIsAddRowOperation({
  inputKey,
  currentTableValue,
  value,
}: {
  readonly inputKey: string;
  readonly currentTableValue?: FormGeneratorTableValue;
  readonly value?: FormBuilderTableValue | FormGeneratorTableValue;
}): boolean {
  return (
    Object.values(value?.[inputKey]?.rows ?? {}).length >
    Object.values(currentTableValue?.[inputKey]?.rows ?? {}).length
  );
}

function transformSelectedDynamicChoiceIntoArray(value: string): string[] {
  return value ? [value] : [];
}

function getSelectedDropdownValuesFromParameters(
  moduleDetailsFieldMapping: ParameterModuleFieldMapping,
  input: FGInputModel,
): SelectedDependantFields {
  const currentValue = getCurrentValue(moduleDetailsFieldMapping, input.inputKey) as string[] | string;
  const dynamicOptionValueSource = toString((input.inputCustomConfig as FGInputSelectModel).dynamicOptionValueSource);

  return {
    [input.inputKey]: [
      {
        dynamicOptionValueSource,
        value: Array.isArray(currentValue) ? currentValue : transformSelectedDynamicChoiceIntoArray(currentValue),
      },
    ],
  };
}

function extractSelectedValues({
  input,
  moduleDetailsFieldMapping,
  rootValue,
  parent,
}: {
  readonly input: FGInputModel;
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
  readonly rootValue?: FGInputModel;
  readonly parent?: FGInputModel;
}): SelectedDependantFields {
  return parent && rootValue
    ? getSelectedTableValuesFromParameters({ moduleDetailsFieldMapping, rootValue, input, parent })
    : getSelectedDropdownValuesFromParameters(moduleDetailsFieldMapping, input);
}

function checkSelectFieldIsDependant({
  inputKey,
  input,
  moduleDetailsFieldMapping,
  rootValue,
  parent,
}: {
  readonly inputKey: string;
  readonly input: FGInputModel;
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
  readonly rootValue?: FGInputModel;
  readonly parent?: FGInputModel;
}): SelectedDependantFields | undefined {
  return (input.inputCustomConfig as FGInputMultiSelectModel | FGInputSelectModel).dynamicOptionsSource === inputKey
    ? extractSelectedValues({ input, moduleDetailsFieldMapping, rootValue, parent })
    : undefined;
}

function getTextValuesFromParameters(
  moduleDetailsFieldMapping: ParameterModuleFieldMapping,
  input: FGInputModel,
): SelectedDependantFields {
  const currentValue = getCurrentValue(moduleDetailsFieldMapping, input.inputKey) as string;
  const dynamicOptionValueSource = toString((input.inputCustomConfig as FGInputSelectModel).dynamicOptionValueSource);

  return {
    [input.inputKey]: [
      {
        dynamicOptionValueSource,
        value: Array.isArray(currentValue) ? currentValue : transformSelectedDynamicChoiceIntoArray(currentValue),
      },
    ],
  };
}

function extractTextFieldValues({
  input,
  moduleDetailsFieldMapping,
  rootValue,
  parent,
}: {
  readonly input: FGInputModel;
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
  readonly rootValue?: FGInputModel;
  readonly parent?: FGInputModel;
}): SelectedDependantFields {
  return parent && rootValue
    ? getSelectedTableValuesFromParameters({ moduleDetailsFieldMapping, rootValue, input, parent })
    : getTextValuesFromParameters(moduleDetailsFieldMapping, input);
}

function checkTextFieldIsDependant({
  inputKey,
  input,
  moduleDetailsFieldMapping,
  rootValue,
  parent,
}: {
  readonly inputKey: string;
  readonly input: FGInputModel;
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
  readonly rootValue?: FGInputModel;
  readonly parent?: FGInputModel;
}): SelectedDependantFields | undefined {
  return (input.inputCustomConfig as FGInputMultiSelectModel | FGInputSelectModel).dynamicOptionsSource === inputKey
    ? extractTextFieldValues({ input, moduleDetailsFieldMapping, rootValue, parent })
    : undefined;
}

function findDependantOnTableSchema({
  inputKey,
  input,
  moduleDetailsFieldMapping,
  rootValue,
}: {
  readonly inputKey: string;
  readonly input: FGInputModel;
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
  readonly rootValue?: FGInputModel;
}): SelectedDependantFields | undefined {
  return (input.inputCustomConfig as FGInputTableModel<FGInputModel>).inputsSchema.reduce((accum, current) => {
    const fields = findNestedFields({
      inputKey,
      input: current,
      moduleDetailsFieldMapping,
      rootValue: rootValue ?? input,
      parent: input,
    });
    return mergeSelectedDependantFields(accum, fields ?? {});
  }, {} as SelectedDependantFields);
}

function findNestedFields({
  inputKey,
  input,
  moduleDetailsFieldMapping,
  rootValue,
  parent,
}: {
  readonly inputKey: string;
  readonly input: FGInputModel;
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
  readonly rootValue?: FGInputModel;
  readonly parent?: FGInputModel;
}): SelectedDependantFields | undefined {
  switch (input.inputCustomConfig.type) {
    case FGInputTypeEnum.MultiSelect:
    case FGInputTypeEnum.Select: {
      return checkSelectFieldIsDependant({ inputKey, input, moduleDetailsFieldMapping, rootValue, parent });
    }
    case FGInputTypeEnum.Table: {
      return findDependantOnTableSchema({ inputKey, input, moduleDetailsFieldMapping, rootValue });
    }
    case FGInputTypeEnum.Text: {
      return checkTextFieldIsDependant({ inputKey, input, moduleDetailsFieldMapping, rootValue, parent });
    }
    default: {
      return undefined;
    }
  }
}

function mergeSelectedDependantFields(
  accum: SelectedDependantFields,
  found: SelectedDependantFields,
): SelectedDependantFields {
  const [foundKey] = Object.keys(found);

  return {
    ...accum,
    ...(accum[foundKey]
      ? {
          [foundKey]: [...accum[foundKey], ...found[foundKey]],
        }
      : found),
  };
}

function searchDependantFieldsPerSections({
  inputKey,
  sections,
  moduleDetailsFieldMapping,
}: {
  readonly inputKey: string;
  readonly sections: FGSectionModel[];
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
}): SelectedDependantFields {
  return sections.reduce((accum, current) => {
    const fields = current.inputs.reduce((accumInputs, currentInput) => {
      const foundField = findNestedFields({ inputKey, input: currentInput, moduleDetailsFieldMapping });
      return mergeSelectedDependantFields(accumInputs, foundField ?? {});
    }, {} as SelectedDependantFields);
    return mergeSelectedDependantFields(accum, fields);
  }, {} as SelectedDependantFields);
}

function getDependantFieldsMapping({
  inputKey,
  formBuilderConfig,
  moduleDetailsFieldMapping,
}: {
  readonly inputKey: string;
  readonly formBuilderConfig: FGBuildModel[];
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
}): SelectedDependantFields {
  return formBuilderConfig.reduce((accum, current) => {
    const fields = searchDependantFieldsPerSections({
      inputKey,
      sections: current.sections,
      moduleDetailsFieldMapping,
    });
    return mergeSelectedDependantFields(accum, fields);
  }, {} as SelectedDependantFields);
}

function notifyNotAllowedFieldModification(shouldDisplayNotification: boolean, errorMessage: string): void {
  if (shouldDisplayNotification) {
    showNotification({
      level: Level.Error,
      content: errorMessage,
    });
  }
}

function findChangedValueInDependants(
  value: string[] | string,
  changedFieldValue: FGGridOnChangeInterceptorOtherAttrs['currentValue'],
  dynamicOptionValueSource: string,
): boolean {
  return Array.isArray(value)
    ? value.includes(toString(changedFieldValue[dynamicOptionValueSource]))
    : isEqual(value, changedFieldValue[dynamicOptionValueSource]);
}

function checkSelectedValueStillExists(
  dependantFieldValue: DependantFieldsInfo[],
  changedFieldValue: FGGridOnChangeInterceptorOtherAttrs['currentValue'],
): boolean {
  return dependantFieldValue.some((selectedValue) =>
    findChangedValueInDependants(selectedValue.value, changedFieldValue, selectedValue.dynamicOptionValueSource),
  );
}

function checkModifiedFieldIsInUse(
  dependantFieldsValues: DependantFieldsInfo[][],
  changedFieldValue: FGGridOnChangeInterceptorOtherAttrs['currentValue'],
  newValue: FGGridOnChangeInterceptorOtherAttrs['newValue'],
): boolean {
  return dependantFieldsValues.some((dependantFieldsValue) => {
    return dependantFieldsValue.some(({ value, dynamicOptionValueSource }) => {
      const foundValue = findChangedValueInDependants(value, changedFieldValue, dynamicOptionValueSource);
      const isDifferent = changedFieldValue[dynamicOptionValueSource] !== newValue?.[dynamicOptionValueSource];
      return foundValue && isDifferent;
    });
  });
}

function checkDynamicValueStillExists(
  dependantFields: SelectedDependantFields,
  otherTableFieldAttributes: FGGridOnChangeInterceptorOtherAttrs,
): boolean {
  const dependantFieldsValues = Object.values(dependantFields);

  return dependantFieldsValues.some((dependantFieldValue) =>
    checkSelectedValueStillExists(dependantFieldValue, otherTableFieldAttributes.currentValue),
  );
}

function verifyIsAllowedLineModification({
  inputKey,
  value,
  dependantFields,
  errorMessage,
  otherAttributes,
}: {
  readonly inputKey: string;
  readonly value: unknown;
  readonly dependantFields: SelectedDependantFields;
  readonly errorMessage: string;
  readonly otherAttributes?: FGOnChangeInterceptorOtherAttrs<FGInputCustomModel>;
}): boolean {
  if (checkIsTableFieldValue(value, inputKey)) {
    const dependantFieldsValues = Object.values(dependantFields);
    const otherTableFieldAttributes = otherAttributes as FGGridOnChangeInterceptorOtherAttrs;
    const isDelete = !otherTableFieldAttributes?.newValue;

    const modifiedValueIsInUse = isDelete
      ? checkDynamicValueStillExists(dependantFields, otherTableFieldAttributes)
      : checkModifiedFieldIsInUse(
          dependantFieldsValues,
          otherTableFieldAttributes.currentValue,
          otherTableFieldAttributes.newValue,
        );

    notifyNotAllowedFieldModification(modifiedValueIsInUse, errorMessage);

    return !modifiedValueIsInUse;
  } else {
    return false;
  }
}

function checkShouldAllowModification({
  inputKey,
  value,
  moduleDetailsFieldMapping,
  formBuilderConfig,
  errorMessage,
  otherAttributes,
}: {
  readonly inputKey: string;
  readonly value: unknown;
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
  readonly formBuilderConfig: FGBuildModel[];
  readonly errorMessage: string;
  readonly otherAttributes?: FGOnChangeInterceptorOtherAttrs<FGInputCustomModel>;
}): boolean {
  if (checkIsTableFieldValue(value, inputKey) && otherAttributes) {
    const dependants = getDependantFieldsMapping({ inputKey, formBuilderConfig, moduleDetailsFieldMapping });

    const hasNoMoreRows = Object.values(value?.[inputKey]?.rows ?? {}).length === 0;

    const hasAnyDependant = checkDynamicValueStillExists(
      dependants,
      otherAttributes as FGGridOnChangeInterceptorOtherAttrs,
    );

    const notAllowedCleaning = hasAnyDependant && hasNoMoreRows;

    notifyNotAllowedFieldModification(notAllowedCleaning, errorMessage);

    return notAllowedCleaning
      ? false
      : verifyIsAllowedLineModification({
          inputKey,
          value,
          dependantFields: dependants,
          errorMessage,
          otherAttributes,
        });
  } else {
    return true;
  }
}

export function checkShouldAllowChangingField({
  inputKey,
  value,
  moduleDetailsFieldMapping,
  formBuilderConfig,
  errorMessage,
  otherAttributes,
}: {
  readonly inputKey: string;
  readonly value: unknown;
  readonly moduleDetailsFieldMapping: ParameterModuleFieldMapping;
  readonly formBuilderConfig: FGBuildModel[];
  readonly errorMessage: string;
  readonly otherAttributes?: FGOnChangeInterceptorOtherAttrs<FGInputCustomModel>;
}): boolean {
  if (checkIsTableFieldValue(value, inputKey)) {
    const currentTableValue = getTableFieldActiveValue(moduleDetailsFieldMapping[inputKey], inputKey);
    const isAddRowOperation = checkIsAddRowOperation({ inputKey, currentTableValue, value });

    return isAddRowOperation
      ? isAddRowOperation
      : checkShouldAllowModification({
          inputKey,
          value,
          moduleDetailsFieldMapping,
          formBuilderConfig,
          errorMessage,
          otherAttributes,
        });
  } else {
    return true;
  }
}

export function extractAllSmartFieldsFieldKeys(
  allSmartFields: Record<string, Record<string, SmartFieldsResource>>,
): string[] {
  return Object.keys(allSmartFields)?.reduce((accum, templateId) => {
    return [...accum, ...Object.keys(allSmartFields?.[templateId] ?? {})];
  }, [] as string[]);
}

export function checkFieldIsSmartFieldByInputKey(
  allSmartFields: Record<string, Record<string, SmartFieldsResource>>,
  selectedTemplateId: string,
  inputKey: string,
): boolean {
  return Object.keys(allSmartFields[selectedTemplateId]).includes(inputKey);
}

export function checkSelectedDifferentTemplate(
  newParameter: ParameterModuleHeaderParameter,
  current?: ParameterModuleHeaderParameter,
): boolean {
  return newParameter.label !== current?.label && newParameter.value !== current?.value;
}
